/**
 * @file pages/Signup.jsx
 * @description Signup page with email/password and Google OAuth registration.
 * Creates Firebase user and syncs with backend API for JWT token generation.
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createUserWithEmailAndPassword, signInWithPopup, GoogleAuthProvider } from 'firebase/auth';
import { login, getCurrentUser } from '../services/api';
import { auth } from '../config/firebase';

const googleProvider = new GoogleAuthProvider();

const Signup = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email || !password || !fullName) {
      alert('Please fill in all fields');
      return;
    }

    try {
      await createUserWithEmailAndPassword(auth, email, password);
      alert('Signup successful!');
      navigate('/');
    } catch (error) {
      alert(`Error: ${error.message}`);
      console.error('Signup error:', error);
    }
  };

  const handleGoogleSignup = async () => {
    try {
      const result = await signInWithPopup(auth, googleProvider);
      const idToken = await result.user.getIdToken();
      
      // Log in to backend with Firebase token
      const response = await login({ token: idToken });
      
      // Get user data from backend
      const userData = await getCurrentUser();
      
      // Store user data in localStorage
      localStorage.setItem('user', JSON.stringify({
        ...userData,
        token: idToken
      }));
      
      alert('Google signup successful!');
      navigate('/');
    } catch (error) {
      console.error('Google signup error:', error);
      const errorMessage = error.response?.data?.message || 
                         error.message || 
                         'Google signup failed. Please try again.';
      alert(`Error: ${errorMessage}`);
    }
  };

  const goToLogin = () => navigate('/login');

  return (
    <div className="h-screen flex items-center justify-center bg-gradient-to-r from-indigo-500 to-purple-600">
      <div className="bg-white p-8 rounded-lg shadow-lg w-full max-w-sm">
        <h2 className="text-2xl font-bold mb-6 text-center">Sign up</h2>
        <form onSubmit={handleSubmit}>
          <input
            type="text"
            placeholder="Full name"
            className="w-full p-3 border rounded-lg mb-4"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
          />
          <input
            type="email"
            placeholder="Email address"
            className="w-full p-3 border rounded-lg mb-4"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <input
            type="password"
            placeholder="Password"
            className="w-full p-3 border rounded-lg mb-4"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <button type="submit" className="w-full bg-indigo-600 text-white p-3 rounded-lg">
            Sign Up
          </button>
        </form>

        <div className="mt-4">
          <p className="text-center text-gray-600 text-sm mb-3">Or sign up with</p>
          <button
            onClick={handleGoogleSignup}
            className="w-full bg-white border border-gray-300 text-gray-700 p-3 rounded-lg flex items-center justify-center space-x-2 hover:bg-gray-50"
          >
            <img src="https://www.google.com/favicon.ico" alt="Google" className="w-5 h-5" />
            <span>Sign up with Google</span>
          </button>
        </div>

        <p className="text-sm text-center mt-4">
          Already have an account?{' '}
          <button onClick={goToLogin} className="text-indigo-600 underline">
            Log In
          </button>
        </p>
      </div>
    </div>
  );
};

export default Signup;

